<script setup lang="ts">
import FinestCountBadge from '@/components/FinestCountBadge.vue'
import { useFeatureFlagStore } from '@/stores/features'
import BankerAccountController from '@controllers/Banks/BankerAccountController'
import BanksDashboardController from '@controllers/Banks/DashboardController'
import PartnerBanksController from '@controllers/Banks/PartnerBanksController'
import BanksProjectsController from '@controllers/Banks/ProjectsController.js'
import CompaniesController from '@controllers/Investors/CompaniesController'
import CreditController from '@controllers/Investors/CreditController'
import DocumentsController from '@controllers/Investors/DocumentsController'
import InvestorsDashboardController from '@controllers/Investors/DashboardController'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import ProfileController from '@controllers/Investors/ProfileController'
import InvestorsProjectsController from '@controllers/Investors/ProjectsController'
import { usePage } from '@inertiajs/vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const page = usePage()
const { enabled } = useFeatureFlagStore()

interface NavLink {
  type?: 'link'
  label: string
  icon: string
  href: string | { url: string }
  active: boolean
  feature_needed?: string
  permission_needed?: string
  badge?: number
}

interface NavHeading {
  type: 'heading'
  label: string
}

type NavEntry = NavLink | NavHeading

const currentPath = computed(() => page.url.split(/[?#]/)[0])
const isBank = computed(() => page.component.startsWith('banks/'))

const isFinancingRequestContext = computed(() =>
  new URLSearchParams(page.url.split('?')[1]?.split('#')[0] ?? '').has('submission_project_id'),
)

const navBadges = computed(
  () =>
    (page.props.navBadges ?? { profile: 0, companies: 0, portfolio: 0, credit: 0 }) as {
      profile: number
      companies: number
      portfolio: number
      credit: number
    },
)

const investorMenu = computed<NavEntry[]>(() => [
  {
    label: t('start'),
    icon: 'pi pi-home',
    href: InvestorsDashboardController(),
    active: currentPath.value.startsWith('/dashboard'),
  },
  {
    type: 'heading',
    label: t('group_financing'),
  },
  {
    label: t('requests'),
    icon: 'pi pi-file-edit',
    href: InvestorsProjectsController.index(),
    active: currentPath.value.startsWith('/financing-requests') || isFinancingRequestContext.value,
  },
  {
    type: 'heading',
    label: t('group_base'),
  },
  {
    label: t('profile'),
    icon: 'pi pi-id-card',
    href: ProfileController(),
    active: currentPath.value.startsWith('/profile'),
    badge: navBadges.value.profile,
  },
  {
    label: t('companies'),
    icon: 'pi pi-building',
    href: CompaniesController.index(),
    active: currentPath.value.startsWith('/companies'),
    badge: navBadges.value.companies,
  },
  {
    label: t('portfolio'),
    icon: 'pi pi-warehouse',
    href: EstateObjectsController.index(),
    active: currentPath.value.startsWith('/properties') && !isFinancingRequestContext.value,
    badge: navBadges.value.portfolio,
  },
  {
    label: t('credit'),
    icon: 'pi pi-chart-line',
    href: CreditController(),
    active: currentPath.value.startsWith('/credit'),
    badge: navBadges.value.credit,
  },
  {
    type: 'heading',
    label: t('group_tools'),
  },
  {
    label: t('documents'),
    icon: 'pi pi-folder-open',
    href: DocumentsController(),
    active: currentPath.value.startsWith('/documents'),
  },
])

const bankMenu = computed<NavLink[]>(() => {
  const userPermissions = page.props.auth?.user?.user_permissions ?? []

  const entries: NavLink[] = [
    {
      label: t('dashboard'),
      icon: 'pi pi-objects-column',
      href: BanksDashboardController(),
      active: currentPath.value === '/dashboard',
    },
    {
      label: t('loan_negotiations'),
      icon: 'pi pi-sync',
      href: BanksProjectsController.index(),
      active: currentPath.value.startsWith('/financing-negotiations'),
      feature_needed: 'loan_negotiations',
    },
    {
      label: t('financing_requests'),
      icon: 'pi pi-bullseye',
      href: BanksProjectsController.suggested(),
      active: currentPath.value.startsWith('/financing-requests'),
      feature_needed: 'financing_requests',
    },
    {
      label: t('user_management'),
      icon: 'pi pi-users',
      href: BankerAccountController.index(),
      active: currentPath.value.startsWith('/user-management'),
      permission_needed: 'manage client users',
    },
    {
      label: t('bank_settings'),
      icon: 'pi pi-building-columns',
      href: PartnerBanksController(),
      active: currentPath.value.startsWith('/bank-profile'),
    },
  ]

  return entries.filter((entry) => {
    if (entry.feature_needed && !enabled(entry.feature_needed)) {
      return false
    }

    if (!entry.permission_needed) {
      return true
    }

    return userPermissions.includes(entry.permission_needed)
  })
})

const menu = computed<NavEntry[]>(() => (isBank.value ? bankMenu.value : investorMenu.value))
</script>

<template>
  <nav :aria-label="t('navigation')">
    <ul class="flex w-full flex-col gap-1">
      <li v-for="item in menu" :key="item.label" class="flex w-full px-2">
        <span
          v-if="item.type === 'heading'"
          class="text-surface-500 w-full px-4 pt-5 pb-1 text-xs font-semibold tracking-wide uppercase">
          {{ item.label }}
        </span>
        <Link
          v-else
          :href="item.href"
          class="group flex w-full items-center gap-3 rounded-lg px-4 py-3 transition"
          :class="
            item.active
              ? 'bg-info-subtle text-brand-blue-1 hover:bg-info-subtle-hover active:bg-info-subtle-active font-semibold'
              : 'text-ink-2 hover:bg-surface-hover hover:text-ink active:bg-surface-active font-normal'
          ">
          <i
            :class="[
              item.icon,
              'text-2xl transition',
              item.active ? 'opacity-100' : 'opacity-75 group-hover:opacity-100',
            ]"
            aria-hidden="true" />
          <div>{{ item.label }}</div>
          <FinestCountBadge :count="item.badge ?? 0" class="ml-auto" />
        </Link>
      </li>
    </ul>
  </nav>
</template>

<i18n>
{
  "de": {
    "navigation": "Hauptnavigation",
    "start": "Start",
    "group_base": "Meine Basis",
    "profile": "Profil",
    "companies": "Unternehmen",
    "portfolio": "Portfolio",
    "credit": "Bonität",
    "group_financing": "Finanzierungen",
    "requests": "Anfragen",
    "group_tools": "Tools",
    "documents": "Dokumente",
    "dashboard": "Dashboard",
    "loan_negotiations": "In Finanzierungsverhandlung",
    "financing_requests": "Finanzierungsanfragen",
    "user_management": "Nutzerverwaltung",
    "bank_settings": "Meine Bank"
  },
  "en": {
    "navigation": "Main navigation",
    "start": "Start",
    "group_base": "My Base",
    "profile": "Profile",
    "companies": "Companies",
    "portfolio": "Portfolio",
    "credit": "Credit",
    "group_financing": "Financing",
    "requests": "Requests",
    "group_tools": "Tools",
    "documents": "Documents",
    "dashboard": "Dashboard",
    "loan_negotiations": "In loan negotiations",
    "financing_requests": "Project suggestions",
    "user_management": "User Management",
    "bank_settings": "My Bank"
  }
}
</i18n>
